import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { ElevatedButton, Spinner } from '@producer/ui-kit';

import { TeamItem } from './team-item';
import { fetchTeams } from '../../teams/stores/team.slice';
import { LoaderContainer } from '../../../routes/project/components/step-container';
import { CreateTeamModal } from '../../../components/dialogs/CreateTeamModal';
import { ProtectedTeamItem } from './protected-team-item';
import { Header } from '../../../components/table/styled-shared-table';
import { TeamContact } from '../../teams/models/team';
import { Character } from '../../../app/entities/character';
import { Contact } from '../../../app/entities/contact';

import type { Step } from '../../../app/entities/step';
import type { Project } from '../../../app/entities/project';
import { openModal } from '../../../core/modal/open-modal';

import styles from './team-list.module.scss';
type TeamListProps = {
  project: Project;
  step: Step;
};

export const TeamList: React.FC<TeamListProps> = observer(({ step, project }) => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchTeams(project._id)
      .then(() => setIsLoading(false))
      .catch(() => setIsLoading(false));
  }, [project._id]);

  if (isLoading) {
    return (
      <LoaderContainer>
        <Spinner />
      </LoaderContainer>
    );
  }

  const characters = step.shots
    .map((shot) => shot.characters)
    .flat()
    .map((character) => character._id);
  const charactersIds = [...new Set(characters)];

  const cast = charactersIds
    ?.map((characterId) => {
      const fullCharacter = Character.getOne(characterId);
      if (!fullCharacter) return;

      if (!fullCharacter.cast) {
        return {
          email: '-',
          phone: '-',
          role: fullCharacter.name,
          contactId: null,
        };
      }

      const contact = Contact.getOne(fullCharacter.cast.contactId);

      return {
        ...fullCharacter.cast,
        email: contact?.email,
        phone: contact?.phone,
        role: fullCharacter.name,
      };
    })
    .filter((cast) => !!cast)
    .map((cast) => ({
      ...cast,
      _id: cast?.contactId,
    })) as TeamContact[];

  const checkIsCrewTeam = (teamId: string) => {
    return project.steps.some((step) => step.info?.teamId === teamId);
  };

  const openCreateTeamModal = () => openModal(CreateTeamModal, { project });

  return (
    <div className={styles['container']}>
      <Header>
        <h1>Teams</h1>

        <ElevatedButton onClick={openCreateTeamModal} text="Add team" />
      </Header>

      <div className={styles['teams-container']}>
        {!!project?.contacts?.length && (
          <ProtectedTeamItem
            name="Executive"
            contacts={project.contacts}
            step={step}
            meta={step.contactsMetaData}
          />
        )}

        {project.teams.map((team) => {
          const isCrewTeam = checkIsCrewTeam(team._id);

          return (
            <TeamItem
              key={team._id}
              team={team}
              step={step}
              spaceId={project.spaceId}
              isCrewTeam={isCrewTeam}
            />
          );
        })}

        {!!cast?.length && (
          <ProtectedTeamItem name="Cast" contacts={cast} step={step} meta={step.contactsMetaData} />
        )}
      </div>
    </div>
  );
});
